import { memo, useEffect, useState } from 'react';
import type { FilterConfigBarItem } from '@rocco/ui/filter-sort/types/filter-config-bar';
import {
    type FilterState,
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchableListFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import { UIConfigurationError } from '@rocco/exceptions/ui-configuration-error';
import { FiltersBarSingleValue } from '@rocco/ui/filter-sort/components/filters-bar/elements/FiltersBarSingleValue';
import { FiltersBarSearchableList } from '@rocco/ui/filter-sort/components/filters-bar/elements/FilterSearchableList';
import { FiltersBarMultipleValues } from '@rocco/ui/filter-sort/components/filters-bar/elements/FiltersBarMultipleValues';
import { FiltersBarPrice } from '@rocco/ui/filter-sort/components/filters-bar/elements/FiltersBarPrice';
import { FiltersBarLocation } from '@rocco/ui/filter-sort/components/filters-bar/elements/FiltersBarLocation';
import { FiltersBarColors } from '@rocco/ui/filter-sort/components/filters-bar/elements/FiltersBarColors';
import { FiltersBarSimpleRange } from '@rocco/ui/filter-sort/components/filters-bar/elements/FiltersBarSimpleRange';

/** Props interface for filter bar element components with typed config and state. */
export interface FilterBarElementProps<T, V> {
    filterConfigItem: T;
    filterState: V;
    onFilterStateUpdate: (state: V, refetch: boolean) => void;
}

interface FiltersBarElementOwnProps {
    filterConfigItem: FilterConfigBarItem;
    filterState: FilterState | null;
}

/**
 * Factory component that renders the appropriate filter UI based on config type.
 * Supports: single value, location, multiple values, searchable, color, range, and price filters.
 *
 * @example
 * <FiltersBarElement filterConfigItem={colorConfig} filterState={colorState} />
 */
export const FiltersBarElement = memo(function FiltersBarElement(
    props: FiltersBarElementOwnProps,
) {
    const { filterConfigItem, filterState } = props;
    const { searchFilters } = useFiltersContext();
    const { filterStates, updateFilter, getUpdatedFilterStates } =
        searchFilters;

    if (filterState === null) {
        throw new UIConfigurationError(
            `Filter has a null state: ${filterConfigItem.filterKey}`,
        );
    }

    const [state, setState] = useState<FilterState>(filterState);

    useEffect(() => {
        setState(filterState);
    }, [filterState]);

    const updateState = (newState: FilterState, refetch: boolean) => {
        setState(newState);

        const newFilterStates = getUpdatedFilterStates(
            filterStates,
            filterConfigItem.filterKey,
            newState,
        );

        updateFilter(newFilterStates, refetch);
    };

    switch (filterConfigItem.type) {
        case 'default':
            if (isSingleValueFilterState(state)) {
                return (
                    <FiltersBarSingleValue
                        filterConfigItem={filterConfigItem}
                        filterState={state}
                        onFilterStateUpdate={updateState}
                    />
                );
            }
            if (isLocationFilterState(state)) {
                return (
                    <FiltersBarLocation
                        filterConfigItem={filterConfigItem}
                        filterState={state}
                        onFilterStateUpdate={updateState}
                    />
                );
            }
            if (isMultipleValuesFilterState(state)) {
                return (
                    <FiltersBarMultipleValues
                        filterConfigItem={filterConfigItem}
                        filterState={state}
                        onFilterStateUpdate={updateState}
                    />
                );
            }
            break;
        case 'searchable':
            if (isSearchableListFilterState(state)) {
                return (
                    <FiltersBarSearchableList
                        filterConfigItem={filterConfigItem}
                        filterState={state}
                        onFilterStateUpdate={updateState}
                    />
                );
            }
            break;
        case 'color':
            if (isMultipleValuesFilterState(state)) {
                return (
                    <FiltersBarColors
                        filterConfigItem={filterConfigItem}
                        filterState={state}
                        onFilterStateUpdate={updateState}
                    />
                );
            }
            break;
        case 'simpleRange':
            if (isRangeFilterState(state)) {
                return (
                    <FiltersBarSimpleRange
                        filterConfigItem={filterConfigItem}
                        filterState={state}
                        onFilterStateUpdate={updateState}
                    />
                );
            }
            break;
        case 'price':
            if (isRangeFilterState(state)) {
                return (
                    <FiltersBarPrice
                        filterConfigItem={filterConfigItem}
                        filterState={state}
                        onFilterStateUpdate={updateState}
                    />
                );
            }
            break;
    }

    return null;
});
